import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { ChevronLeft, ChevronRight } from "@untitledui/icons"

import type { ApiEstimateImage } from "@/lib/api/types"
import { sideLabel } from "@/lib/estimates/catalog"
import { cx } from "@/lib/utils/cx"

// One photo at a time, as big as the column allows, with a filmstrip of
// the others under it. The photo is the lens the estimate is read through.

const MIN_H = 320
const MAX_H = 720

export function PhotoViewer({
  images,
  index,
  onIndex,
  hug = false,
  thumb = "sm",
}: {
  images: ApiEstimateImage[]
  index: number
  onIndex: (i: number) => void
  /** Size the box to the photo instead of the column — no letterbox. */
  hug?: boolean
  thumb?: "sm" | "lg"
}) {
  const reduce = useReducedMotion()
  const boxRef = useRef<HTMLDivElement>(null)
  const [boxW, setBoxW] = useState(0)
  const [maxH, setMaxH] = useState(560)
  const [aspects, setAspects] = useState<Record<number, number>>({})

  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    const measure = () => {
      setBoxW((el.parentElement ?? el).clientWidth)
      const lg = window.innerWidth >= 1024
      // Desktop: leave room for the filmstrip and the list under it.
      const fit = window.innerHeight - el.getBoundingClientRect().top - window.scrollY - 96
      // Beside a list (hug) the photo may take the whole height; above a list it leaves room for it.
      const desk = hug ? fit : Math.min(fit, window.innerHeight * 0.55)
      setMaxH(Math.min(MAX_H, Math.round(lg ? desk : window.innerHeight * 0.55)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el.parentElement ?? el)
    window.addEventListener("resize", measure)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [hug])

  const image = images[index]
  const aspect = image ? (aspects[image.id] ?? 4 / 3) : 4 / 3
  const height = Math.max(MIN_H, Math.min(maxH, boxW ? boxW / aspect : maxH))
  const hugW = hug && boxW ? Math.min(boxW, Math.round(height * aspect)) : undefined

  const learn = (id: number, el: HTMLImageElement) => {
    if (el.naturalWidth && el.naturalHeight) {
      setAspects((a) => (a[id] ? a : { ...a, [id]: el.naturalWidth / el.naturalHeight }))
    }
  }

  if (images.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 px-6 py-16 text-center">
        <p className="text-[14px] font-medium text-zinc-900">No photos on this estimate.</p>
        <p className="mt-1 text-[13px] text-zinc-500">Parts can still be added to the estimate.</p>
      </div>
    )
  }

  const prev = () => onIndex(Math.max(0, index - 1))
  const next = () => onIndex(Math.min(images.length - 1, index + 1))

  return (
    <div>
      <div
        ref={boxRef}
        className={cx("relative overflow-hidden rounded-xl bg-zinc-100 ring-1 ring-zinc-200", hug && "mx-auto")}
        style={{ height, width: hugW }}
      >
        <AnimatePresence initial={false}>
          {image && (
            <motion.img
              key={image.id}
              src={image.src}
              alt={image.description ?? `${sideLabel(image.side)} view`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0.1 : 0.15, ease: [0.23, 1, 0.32, 1] }}
              onLoad={(e) => learn(image.id, e.currentTarget)}
              ref={(el) => { if (el?.complete) learn(image.id, el) }}
              className="absolute inset-0 size-full select-none object-contain"
              draggable={false}
            />
          )}
        </AnimatePresence>

        {image && (
          <span className="pointer-events-none absolute left-3 top-3 flex h-7 items-center gap-1.5 rounded-md bg-zinc-900/80 px-2 text-[11.5px] font-semibold text-white backdrop-blur-sm">
            <span className="tabular-nums">Photo {index + 1} of {images.length}</span>
            <span className="text-white/50">·</span>
            <span>{sideLabel(image.side)}</span>
          </span>
        )}

        {images.length > 1 && (
          <>
            <Arrow side="left" onClick={prev} disabled={index === 0} />
            <Arrow side="right" onClick={next} disabled={index === images.length - 1} />
          </>
        )}
      </div>

      {images.length > 1 && (
        <Filmstrip images={images} index={index} onIndex={onIndex} size={thumb} center={hug} />
      )}
    </div>
  )
}

function Arrow({ side, onClick, disabled }: { side: "left" | "right"; onClick: () => void; disabled: boolean }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === "left" ? "Previous photo" : "Next photo"}
      className={cx(
        "absolute top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/95 text-zinc-900 shadow-md ring-1 ring-zinc-900/10 transition-opacity duration-150",
        "hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/50",
        "disabled:pointer-events-none disabled:opacity-0",
        side === "left" ? "left-3" : "right-3",
      )}
    >
      <Icon className="size-5" />
    </button>
  )
}

function Filmstrip({
  images,
  index,
  onIndex,
  size,
  center,
}: {
  images: ApiEstimateImage[]
  index: number
  onIndex: (i: number) => void
  size: "sm" | "lg"
  center: boolean
}) {
  const stripRef = useRef<HTMLDivElement>(null)

  // Keep the current thumbnail in view when the strip overflows.
  useEffect(() => {
    const el = stripRef.current?.children[index] as HTMLElement | undefined
    el?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" })
  }, [index])

  return (
    <div ref={stripRef} role="tablist" aria-label="Photos" className={cx("mt-3 flex gap-2 overflow-x-auto pb-1", center && "justify-center")}>
      {images.map((img, i) => {
        const current = i === index
        return (
          <button
            key={img.id}
            type="button"
            role="tab"
            aria-selected={current}
            aria-label={`Photo ${i + 1}, ${sideLabel(img.side)}`}
            title={sideLabel(img.side)}
            onClick={() => onIndex(i)}
            className={cx(
              "relative shrink-0 overflow-hidden rounded-md bg-zinc-100 transition-[box-shadow,opacity] duration-150",
              size === "lg" ? "h-[84px] w-[112px]" : "h-[54px] w-[72px]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/50",
              current ? "ring-2 ring-zinc-900" : "opacity-70 ring-1 ring-zinc-200 hover:opacity-100",
            )}
          >
            <img src={img.src} alt="" className="size-full object-cover" loading="lazy" draggable={false} />
          </button>
        )
      })}
    </div>
  )
}
